import { alpha, Box, Divider, Drawer, IconButton, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BsBoxArrowRight, BsX } from "react-icons/bs";

import { useDrawer } from "../../contexts/drawerContext";
import { useUser } from "../../contexts/userContext";
import ActionButtonWithIcon from "../Common/ActionButtonWithIcon";
import CustomChip from "../Common/CustomChip";
import IconWrapper from "../Common/IconWrapper";
import LanguageSelector from "./LanguageSelector";

import ThemeSelector from "./ThemeSelector";
import { logout } from "../../api/endpoints/generalAPI";

const CloseIcon = IconWrapper(BsX);

/**
 * Simple drawer header with title and close button
 */
function DrawerHeader({ text, onClose }: { text: string; onClose: () => void }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderBottom: `1px solid ${theme.palette.divider}`
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {text}
            </Typography>
            <IconButton onClick={onClose} size="small">
                <CloseIcon style={{ fontSize: 24 }} />
            </IconButton>
        </Box>
    );
}

/**
 * `MenuDrawer` is a right-anchored slide-out drawer component used for app-wide navigation.
 *
 * Features:
 * - Responsive drawer width based on screen size
 * - Theme and language settings
 * - User info and logout
 *
 * @component
 */
function MenuDrawer() {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "menu" });
    const { user } = useUser();
    const { isDrawerOpen, closeDrawer } = useDrawer();

    const handleLogout = () => logout();
    return (
        <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={closeDrawer}
            PaperProps={{
                sx: {
                    width: { xs: "95%", sm: 300 },
                    height: "100%",
                    backgroundColor: theme.palette.drawerMenuBackground,
                    borderRadius: "12px 0px 0px 12px",
                    pt: 0
                }
            }}
        >
            <DrawerHeader text={t("menu")} onClose={closeDrawer} />

            <Box sx={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "flex-start", gap: 1, paddingInline: 2, paddingTop: 2, flex: 1 }}>
                <ThemeSelector />

                <LanguageSelector />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", mt: 2, mb: 1, width: "100%" }}>
                <Divider sx={{ my: 2, width: "100%" }} />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                        paddingInline: 2
                    }}
                >
                    <CustomChip sx={{ height: 32, fontSize: "0.875rem", color: alpha(theme.palette.text.primary, 0.7) }}>{user?.userName ?? ""}</CustomChip>

                    <ActionButtonWithIcon text={t("logout")} icon={BsBoxArrowRight} onClick={handleLogout} />
                </Box>
            </Box>
        </Drawer>
    );
}

export default MenuDrawer;
